import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useQueryParams } from 'hooks/useQueryParams';
import { useWindowSize } from 'hooks/useWindowSize';
import { Wrapper, Container } from 'components/Layout/Layout';
import { colors } from 'global/colors';
import { tileImages } from 'assets/tileImages/tileImages';
import { Images, GIFs } from 'assets/images';
import { userRoles } from 'constants/users';

import { ButtonsContainer } from 'components/Button/RectangleButton';
import { LinkButton } from 'components/Button/Button';
import CarouselLanguage from './component/CarouselLanguage';

import {
  SectionContentContainer,
  SectionBackground,
  HeaderContainer,
  HeaderContent,
  HeaderText,
  Title,
  Paragraph,
  FatText,
  LanguageBox,
  LanguageBoxIconContainer,
  LanguageButton,
  LaptopWrapper,
  LaptopIframe,
  LaptopImage,
  MarketingPointsContainer,
  MarketingPointContainer,
  MarketingPointParagraph,
  MarketingPointImage,
  MarketingPointText,
  CodingCharactersGIF,
  FunForAllImagesContainer,
  FunForAllLink,
  FunForAllGIF,
  FunForAllImage,
  LearnFromPeersImage,
  LoginBox,
  LoginBoxContent,
  Link,
  LaptopContainer,
} from './styles';

import { useAppContext } from 'hooks/useContext';
import { NOT_LOGGED_IN_BUTTONS } from 'components/HeaderCommunity/strings';
import { content } from './content';
import LandingAnimation from './LandingAnimation';
import { lightModeHeaderBackground } from 'global/themeLight';

const getLanguages = () => {
  return [
    { label: 'Python', value: 'python', icon: <i className="devicon-python-plain colored mr-1"></i> },
    { label: 'C#', value: 'c_sharp', icon: <i className="devicon-csharp-plain colored mr-1"></i> },
    { label: 'C', value: 'c', icon: <i className="devicon-c-plain colored mr-1"></i> },
    { label: 'Go', value: 'go', icon: <i className="devicon-go-original-wordmark colored mr-1"></i> },
    { label: 'Java', value: 'java', icon: <i className="devicon-java-plain colored mr-1"></i> },
    { label: 'C++', value: 'cpp', icon: <i className="devicon-cplusplus-plain colored mr-1"></i> },
    { label: 'JS', value: 'javascript', icon: <i className="devicon-javascript-plain colored mr-1"></i> },
    { label: 'Kotlin', value: 'kotlin', icon: <i className="devicon-kotlin-plain colored mr-1"></i> },
    { label: 'PHP', value: 'php', icon: <i className="devicon-php-plain colored mr-1"></i> },
    { label: 'Ruby', value: 'ruby', icon: <i className="devicon-ruby-plain colored mr-1"></i> },
    { label: 'Rust', value: 'rust', icon: <i className="devicon-rust-plain colored mr-1"></i> },
    { label: 'Scala', value: 'scala', icon: <i className="devicon-scala-plain colored mr-1"></i> },
    { label: 'TS', value: 'typescript', icon: <i className="devicon-typescript-plain colored mr-1"></i> },
  ];
};

const StartToCodeLink = `/exercise/62879ac8a07f5aeb11059ee9`;

export default function Landing() {
  const { 'no-redirect': noRedirect } = useQueryParams();
  const { width } = useWindowSize();
  const navigate = useNavigate();

  const { language, isDarkMode } = useAppContext();

  const { userRole } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!noRedirect && userRole && userRole !== userRoles.CANDIDATE) navigate('/tests');
  }, [noRedirect, userRole, navigate]);

  const getContent = (content) => {
    return content[language] || content['en'] || '';
  };

  const startToCodeButton = (
    <LinkButton wide href={StartToCodeLink}>
      {getContent(content.StartToCode).toUpperCase()}
    </LinkButton>
  );

  const renderLandingHeader = () => (
    <SectionBackground bgColor={!isDarkMode && lightModeHeaderBackground}>
      <LandingAnimation />
      <Container column center>
        <HeaderContainer>
          <HeaderContent>
            <HeaderText>
              {getContent(content.AreYouReadyTo)}
              <br />
              <FatText>{getContent(content.StartToCode)}?</FatText>
            </HeaderText>
            <Paragraph>{getContent(content.AreYouReadyToParagraph)}</Paragraph>
            <ButtonsContainer padding="0 0 3em 0">{startToCodeButton}</ButtonsContainer>
            <HeaderText small>{getContent(content.UpYourCode)}</HeaderText>
            <Paragraph>{getContent(content.UpYourCodeParagraph)}</Paragraph>
          </HeaderContent>
        </HeaderContainer>
      </Container>
    </SectionBackground>
  );

  const languageBox = (language, index) => (
    <LanguageBox
      isDarkMode={isDarkMode}
      key={index}
      onClick={() => localStorage.setItem('fs-editor-default-language', language.value)}
      href={StartToCodeLink}
    >
      <LanguageBoxIconContainer>{language.icon}</LanguageBoxIconContainer>
      <LanguageButton isDarkMode={isDarkMode}>
        {getContent(content.StartWith)} {language.label}
      </LanguageButton>
    </LanguageBox>
  );

  const renderLanguageBoxes = () => (
    <SectionBackground topColor={!isDarkMode && lightModeHeaderBackground} bottomColor={isDarkMode && colors.darkGray5}>
      <Container usePadding="0" column center>
        <CarouselLanguage
          key={isDarkMode}
          isDarkMode={isDarkMode}
          items={getLanguages().map((language, index) => languageBox(language, index))}
        />
      </Container>
    </SectionBackground>
  );

  const renderFunForAll = () => (
    <SectionBackground bgColor={isDarkMode && colors.darkGray5}>
      <LaptopWrapper>
        <LaptopContainer>
          <LaptopImage src={Images.laptop} loading="lazy" alt="" />
          <LaptopIframe
            title="FS Code Challenge With Agreat"
            src="https://player.vimeo.com/video/711555773?h=3785db0fdf&autoplay=1&muted=1&loop=1"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
          ></LaptopIframe>
        </LaptopContainer>
      </LaptopWrapper>
      <Container column={width < 725}>
        <FunForAllImagesContainer>
          <FunForAllImage src={Images.funCodingIcons} loading="lazy" alt="" />
          <FunForAllLink href={StartToCodeLink}>
            <FunForAllGIF src={GIFs.startToCode} loading="lazy" alt="" />
          </FunForAllLink>
        </FunForAllImagesContainer>
        <SectionContentContainer>
          <Title>{getContent(content.FunForAllH)}</Title>
          <Paragraph>{getContent(content.FunForAllP1)}</Paragraph>
          <Paragraph>{getContent(content.FunForAllP2)}</Paragraph>
        </SectionContentContainer>
      </Container>
    </SectionBackground>
  );

  const renderHowDoesItWork = () => (
    <SectionBackground bgColor={!isDarkMode && lightModeHeaderBackground}>
      <Container column center>
        <Title>{getContent(content.HowDoesItWork)}</Title>
        <MarketingPointsContainer>
          <MarketingPointContainer>
            <MarketingPointImage src={tileImages.peopleTile} loading="lazy" alt="" />
            <MarketingPointText isDarkMode={isDarkMode}>{getContent(content.MembershipH)}</MarketingPointText>
            <MarketingPointParagraph>{getContent(content.MembershipP)}</MarketingPointParagraph>
          </MarketingPointContainer>
          <MarketingPointContainer>
            <MarketingPointImage src={tileImages.chartTile} loading="lazy" alt="" />
            <MarketingPointText isDarkMode={isDarkMode}>{getContent(content.PotentialH)}</MarketingPointText>
            <MarketingPointParagraph>{getContent(content.PotentialP)}</MarketingPointParagraph>
          </MarketingPointContainer>
          <MarketingPointContainer>
            <MarketingPointImage src={tileImages.coffeeTile} loading="lazy" alt="" />
            <MarketingPointText isDarkMode={isDarkMode}>{getContent(content.FreeH)}</MarketingPointText>
            <MarketingPointParagraph>{getContent(content.FreeP)}</MarketingPointParagraph>
          </MarketingPointContainer>
        </MarketingPointsContainer>
        <ButtonsContainer alignCenter>{startToCodeButton}</ButtonsContainer>
      </Container>
    </SectionBackground>
  );

  const renderLearnFromPeers = () => (
    <SectionBackground topColor={!isDarkMode && lightModeHeaderBackground} bottomColor={isDarkMode && colors.darkGray5}>
      <Container column={width < 725} center>
        <SectionContentContainer>
          <Title>{getContent(content.LearnFromPeersH)}</Title>
          <Paragraph>{getContent(content.LearnFromPeersP1)}</Paragraph>
          <Paragraph>{getContent(content.LearnFromPeersP2)}</Paragraph>
          {startToCodeButton}
        </SectionContentContainer>
        <SectionContentContainer first>
          <LearnFromPeersImage
            src={isDarkMode ? Images.learnFromPeersDark : Images.learnFromPeers}
            loading="lazy"
            alt={getContent(content.LearnFromPeersH)}
          />
        </SectionContentContainer>
      </Container>
    </SectionBackground>
  );

  const renderSignUp = () => (
    <SectionBackground bgColor={isDarkMode && colors.darkGray5}>
      <Container column={width < 725}>
        <LoginBox>
          <SectionContentContainer center>
            <CodingCharactersGIF src={GIFs.codingCharacters} loading="lazy" alt="" />{' '}
          </SectionContentContainer>
          <LoginBoxContent>
            <Paragraph textColor={colors.white}>{getContent(content.ImproveYourChance)}</Paragraph>
            <ButtonsContainer>
              {NOT_LOGGED_IN_BUTTONS.map((btn, index) => (
                <Link $hasBorder={btn.style === 'primary'} key={index} href={btn.to}>
                  {btn[language]}
                </Link>
              ))}
            </ButtonsContainer>
          </LoginBoxContent>
        </LoginBox>
      </Container>
    </SectionBackground>
  );

  return (
    <Wrapper isNewPage>
      {renderLandingHeader()}
      {renderLanguageBoxes()}
      {renderFunForAll()}
      {renderHowDoesItWork()}
      {renderLearnFromPeers()}
      {renderSignUp()}
    </Wrapper>
  );
}
